import * as React from "react";
import {
  makeStyles,
  Text,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  Card,
  Button,
  Input,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Label,
} from "@fluentui/react-components";
import { EditRegular, SettingsRegular } from "@fluentui/react-icons";
import { supabase } from "../../services/supabase";

interface AppConfigRow {
  key: string;
  value: string;
  description: string | null;
  updated_at: string | null;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "28px",
    height: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1e293b",
    letterSpacing: "-0.5px",
  },
  card: {
    padding: "0",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow:
      "0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03)",
    border: "1px solid #f1f5f9",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "64px",
    backgroundColor: "#ffffff",
  },
  emptyState: {
    textAlign: "center",
    padding: "64px",
    color: "#94a3b8",
    fontSize: "14px",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  keyCell: {
    fontFamily: "monospace",
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
  },
  valueCell: {
    fontFamily: "monospace",
    fontSize: "13px",
    color: "#059669",
  },
  descCell: {
    fontSize: "13px",
    color: "#64748b",
  },
  timeCell: {
    fontSize: "12px",
    color: "#94a3b8",
  },
  editButton: {
    minWidth: "32px",
  },
  dialogField: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "16px",
  },
  dialogInput: {
    width: "100%",
  },
  statsRow: {
    display: "flex",
    gap: "16px",
  },
  statCard: {
    flex: 1,
    padding: "20px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow:
      "0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03)",
    border: "1px solid #f1f5f9",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  statLabel: {
    fontSize: "12px",
    color: "#64748b",
    fontWeight: "500",
  },
  statValue: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1e293b",
    fontFamily: "monospace",
  },
});

export const ConfigPage: React.FC = () => {
  const styles = useStyles();
  const [configs, setConfigs] = React.useState<AppConfigRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editItem, setEditItem] = React.useState<AppConfigRow | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const loadConfigs = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("app_config")
        .select("*")
        .order("key");
      if (error) throw error;
      setConfigs(data || []);
    } catch (err) {
      console.error("Failed to load configs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleEdit = (item: AppConfigRow) => {
    setEditItem(item);
    setEditValue(item.value);
  };

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("app_config")
        .update({ value: editValue, updated_at: new Date().toISOString() })
        .eq("key", editItem.key);
      if (error) throw error;
      setEditItem(null);
      await loadConfigs();
    } catch (err) {
      console.error("Failed to save config:", err);
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (ts: string | null) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleString();
  };

  // Extract primary & fallback hosts for stat cards
  const primaryHost =
    configs.find((c) => c.key === "worker_primary_host")?.value || "-";
  const fallbackHost =
    configs.find((c) => c.key === "worker_fallback_host")?.value || "-";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <SettingsRegular style={{ fontSize: "24px", color: "#64748b" }} />
          <Text className={styles.title}>Config</Text>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <Card className={styles.statCard}>
          <Text className={styles.statLabel}>Primary Host</Text>
          <Text className={styles.statValue}>{primaryHost}</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={styles.statLabel}>Fallback Host</Text>
          <Text className={styles.statValue}>{fallbackHost}</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={styles.statLabel}>Total Config Items</Text>
          <Text className={styles.statValue}>{configs.length}</Text>
        </Card>
      </div>

      {/* Table */}
      <Card className={styles.card}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="medium" label="Loading config..." />
          </div>
        ) : configs.length === 0 ? (
          <div className={styles.emptyState}>
            No config items found. Create the app_config table in Supabase.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Key</TableHeaderCell>
                <TableHeaderCell>Value</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableHeaderCell>Updated</TableHeaderCell>
                <TableHeaderCell style={{ width: "60px" }}></TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((item) => (
                <TableRow key={item.key} className={styles.tableRow}>
                  <TableCell>
                    <Text className={styles.keyCell}>{item.key}</Text>
                  </TableCell>
                  <TableCell>
                    <Text className={styles.valueCell}>{item.value}</Text>
                  </TableCell>
                  <TableCell>
                    <Text className={styles.descCell}>
                      {item.description || "-"}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text className={styles.timeCell}>
                      {formatTime(item.updated_at)}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      className={styles.editButton}
                      onClick={() => handleEdit(item)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editItem !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setEditItem(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Edit Config</DialogTitle>
            <DialogContent>
              <div className={styles.dialogField}>
                <Label weight="semibold">Key</Label>
                <Input value={editItem?.key || ""} disabled className={styles.dialogInput} />
              </div>
              <div className={styles.dialogField}>
                <Label weight="semibold">Value</Label>
                <Input
                  value={editValue}
                  onChange={(_, data) => setEditValue(data.value)}
                  className={styles.dialogInput}
                />
              </div>
              {editItem?.description && (
                <div className={styles.dialogField}>
                  <Label weight="semibold">Description</Label>
                  <Text className={styles.descCell}>
                    {editItem.description}
                  </Text>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setEditItem(null)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={saving || editValue === editItem?.value}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
